import { Suspense, lazy, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { PageHeader } from "../components/PageHeader";
import { PriorityBadge } from "../components/Badges";
import RadarPratiche from "../components/RadarPratiche";
import CruscottoScadenze from "../components/CruscottoScadenze";
import AvvisoBackup from "../components/AvvisoBackup";
import AvvisoSopralluoghi from "../components/AvvisoSopralluoghi";
import AdempimentiImminenti from "../components/AdempimentiImminenti";
const DashboardCharts = lazy(() => import("../components/DashboardCharts"));
import { eur, eur0 } from "../lib/impresa";
import {
  ClipboardText,
  UsersThree,
  Warning,
  TrendUp,
  Clock,
  CheckCircle,
  Sparkle,
  Plus,
  Bell,
  ArrowUpRight,
  ArrowDownRight,
  PauseCircle,
  Hourglass,
  CurrencyEur,
  Buildings,
  HardHat,
  CalendarBlank,
  CaretRight,
} from "@phosphor-icons/react";

const fmtEUR = eur0;
const fmtEUR2 = eur;

function HeroKPI({ label, value, sub, icon: Icon, trend, tone = "default", testid }) {
  const toneClasses = {
    default: "border-slate-200",
    green: "border-l-4 border-l-emerald-600",
    amber: "border-l-4 border-l-amber-500",
    dark: "bg-slate-900 text-white border-slate-900",
  };
  const isDark = tone === "dark";
  return (
    <Card className={`p-6 shadow-none ${toneClasses[tone]}`} data-testid={testid}>
      <div className="flex items-start justify-between mb-3">
        <div className={`text-[10px] font-mono uppercase tracking-[0.2em] ${isDark ? "text-slate-400" : "text-slate-500"}`}>{label}</div>
        <div className={`h-8 w-8 rounded-md flex items-center justify-center ${
          isDark ? "bg-white/10 text-white" :
          tone === "green" ? "bg-emerald-50 text-emerald-700" :
          tone === "amber" ? "bg-amber-50 text-amber-700" : "bg-slate-100 text-slate-700"
        }`}>
          <Icon size={16} weight="regular" />
        </div>
      </div>
      <div className={`font-heading text-3xl lg:text-4xl font-bold tabular-nums leading-none ${isDark ? "text-white" : "text-slate-900"}`}>
        {value}
      </div>
      {sub && <div className={`text-xs mt-2 ${isDark ? "text-slate-400" : "text-slate-500"}`}>{sub}</div>}
      {trend && (
        <div className={`inline-flex items-center gap-1 mt-3 text-xs font-medium ${
          trend.up ? "text-emerald-700" : "text-red-700"
        }`}>
          {trend.up ? <ArrowUpRight size={12} weight="bold" /> : <ArrowDownRight size={12} weight="bold" />}
          {trend.label}
        </div>
      )}
    </Card>
  );
}

function StatoPill({ label, count, icon: Icon, tone, testid }) {
  const tones = {
    blue: "bg-blue-50 text-blue-700 border-blue-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
    green: "bg-emerald-50 text-emerald-700 border-emerald-200",
    red: "bg-red-50 text-red-700 border-red-200",
  };
  return (
    <Card className={`p-5 border ${tones[tone]} shadow-none`} data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <Icon size={16} weight="regular" />
        <div className="text-[10px] font-mono uppercase tracking-[0.2em]">{label}</div>
      </div>
      <div className="font-heading text-3xl font-bold tabular-nums">{count}</div>
    </Card>
  );
}

function relativeDay(giorni) {
  if (giorni === 0) return { label: "Oggi", tone: "amber" };
  if (giorni < 0) return { label: `In ritardo di ${Math.abs(giorni)} ${Math.abs(giorni) === 1 ? "giorno" : "giorni"}`, tone: "red" };
  if (giorni === 1) return { label: "Domani", tone: "amber" };
  if (giorni <= 7) return { label: `Fra ${giorni} giorni`, tone: "amber" };
  return { label: `Fra ${giorni} giorni`, tone: "slate" };
}

const FILTERS_STORAGE_KEY = "dashboard_filters_v1";

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [members, setMembers] = useState([]);

  // Restore persisted filters on mount
  const persisted = (() => {
    try { return JSON.parse(localStorage.getItem(FILTERS_STORAGE_KEY) || "{}"); } catch { return {}; }
  })();

  const [anno, setAnno] = useState(persisted.anno || new Date().getFullYear());
  const [professionista, setProfessionista] = useState(persisted.professionista || "all");
  const [tipoPratica, setTipoPratica] = useState(persisted.tipoPratica || "all");
  const [reportOpen, setReportOpen] = useState(false);
  const [reportYear, setReportYear] = useState(new Date().getFullYear());
  const [report, setReport] = useState("");
  const [reportLoading, setReportLoading] = useState(false);

  // Mese dettagliato
  const [monthDialog, setMonthDialog] = useState(null);
  const [monthFatture, setMonthFatture] = useState([]);
  const [monthLoading, setMonthLoading] = useState(false);
  const [durcAlerts, setDurcAlerts] = useState([]);
  const [scadenzeProf, setScadenzeProf] = useState([]);
  const [watchdogAlerts, setWatchdogAlerts] = useState([]);
  const [pratiche, setPratiche] = useState([]);
  const [appuntamenti, setAppuntamenti] = useState([]);
  const [tipiIncarico, setTipiIncarico] = useState([]);
  const [clientiMap, setClientiMap] = useState({});

  const load = () => {
    const qs = new URLSearchParams();
    qs.set("anno", String(anno));
    if (professionista && professionista !== "all") qs.set("professionista_id", professionista);
    if (tipoPratica && tipoPratica !== "all") qs.set("tipo_pratica", tipoPratica);
    api.get(`/dashboard/home?${qs.toString()}`).then((r) => { setStats(r.data.stats); setDurcAlerts(r.data.durc_alerts || []); setScadenzeProf(r.data.scadenze_professionali || []); setWatchdogAlerts(r.data.watchdog || []); }).catch(() => {});
    api.get(`/pratiche`).then((r) => setPratiche(r.data || [])).catch(() => {});
    api.get(`/appuntamenti`).then((r) => setAppuntamenti(r.data || [])).catch(() => {});
    api.get(`/settings/tipi-incarico`).then((r) => setTipiIncarico(r.data || [])).catch(() => {});
    api.get(`/clienti`).then((r) => {
      const m = {};
      (r.data || []).forEach((c) => {
        m[c.id] = c.ragione_sociale || `${c.cognome || ""} ${c.nome || ""}`.trim() || "Cliente";
      });
      setClientiMap(m);
    }).catch(() => {});
  };
  useEffect(load, [anno, professionista, tipoPratica]);

  // Persist filters
  useEffect(() => {
    localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify({ anno, professionista, tipoPratica }));
  }, [anno, professionista, tipoPratica]);

  useEffect(() => {
    api.get("/studio/me").then((r) => setMembers(r.data.members || [])).catch(() => {});
  }, []);

  const openMonth = async (payload) => {
    if (!payload) return;
    const chiave = payload.chiave;
    setMonthDialog(payload);
    setMonthLoading(true);
    try {
      const { data } = await api.get("/fatture");
      const filtered = data.filter((f) => (f.data || "").startsWith(chiave));
      setMonthFatture(filtered);
    } catch { toast.error("Errore caricamento fatture"); }
    setMonthLoading(false);
  };

  const generateReport = async () => {
    setReportLoading(true);
    setReport("");
    try {
      const { data } = await api.post(`/ai/studio-report?anno=${reportYear}`);
      setReport(data.report);
      toast.success("Report generato");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore report");
    }
    setReportLoading(false);
  };

  const scadenze = stats?.prossime_scadenze || [];
  const anni = stats?.anni_disponibili || [new Date().getFullYear()];
  const meseTop = stats?.mese_prodotto_massimo;

  // ---- Derived operative lists ----
  const cantiereSlugSet = new Set((tipiIncarico || []).filter((t) => t.cantiere).map((t) => t.slug));
  const pratichePendenti = pratiche.filter((p) => p.stato !== "completata" && p.stato !== "annullata");
  const pratcheDaFare = pratichePendenti
    .filter((p) => p.stato === "da_fare")
    .sort((a, b) => (b.priorita === "alta" ? 1 : 0) - (a.priorita === "alta" ? 1 : 0))
    .slice(0, 6);
  // Cantiere: preferisci flag esplicito is_cantiere, fallback su tipo (compat legacy)
  const isCantierePratica = (p) => p.is_cantiere === true ? true : p.is_cantiere === false ? false : cantiereSlugSet.has(p.tipo);
  const cantieriAttivi = pratichePendenti
    .filter((p) => isCantierePratica(p) && p.stato === "in_corso")
    .sort((a, b) => (a.data_fine_lavori_presunta || "9999") < (b.data_fine_lavori_presunta || "9999") ? -1 : 1)
    .slice(0, 6);
  const now = new Date();
  const in7Days = new Date(now.getTime() + 7 * 86400000);
  const prossimiAppuntamenti = (appuntamenti || [])
    .filter((a) => { const d = new Date(a.data_ora); return d >= now && d <= in7Days; })
    .sort((a, b) => new Date(a.data_ora) - new Date(b.data_ora))
    .slice(0, 6);

  const statoBadge = {
    da_fare: "bg-red-100 text-red-800",
    in_corso: "bg-yellow-100 text-yellow-800",
    in_attesa: "bg-blue-100 text-blue-800",
    completata: "bg-emerald-100 text-emerald-800",
  };
  const statoLabel = { da_fare: "Da fare", in_corso: "In corso", in_attesa: "In attesa", completata: "Completata" };
  const fmtDayShort = (iso) => { if (!iso) return "—"; try { const d = new Date(iso); return d.toLocaleDateString("it-IT", { day: "2-digit", month: "short" }); } catch { return iso; } };
  const fmtDayTime = (iso) => { if (!iso) return "—"; try { const d = new Date(iso); return `${d.toLocaleDateString("it-IT", { weekday: "short", day: "2-digit", month: "short" })} · ${d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}`; } catch { return iso; } };

  return (
    <div className="p-4 md:p-8 w-full max-w-[1400px] mx-auto" data-testid="dashboard-page">
      <PageHeader
        eyebrow="Panoramica"
        title="Dashboard"
        description="Panoramica del tuo studio tecnico"
        actions={
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <Select value={professionista} onValueChange={setProfessionista}>
              <SelectTrigger className="w-[170px]" data-testid="filter-professionista">
                <SelectValue placeholder="Tutti i tecnici" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti i tecnici</SelectItem>
                {members.map((m) => (
                  <SelectItem key={m.id} value={m.id}>
                    {m.name} · {m.qualifica_label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={tipoPratica} onValueChange={setTipoPratica}>
              <SelectTrigger className="w-[150px]" data-testid="filter-tipo">
                <SelectValue placeholder="Tutte le tipologie" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutte le tipologie</SelectItem>
                <SelectItem value="generica">Generica</SelectItem>
                <SelectItem value="catasto">Catasto</SelectItem>
                <SelectItem value="urbanistica">Urbanistica</SelectItem>
                <SelectItem value="condono">Condono</SelectItem>
                <SelectItem value="perizia">Perizia</SelectItem>
                <SelectItem value="successione">Successione</SelectItem>
                <SelectItem value="scia">SCIA</SelectItem>
                <SelectItem value="cila">CILA</SelectItem>
              </SelectContent>
            </Select>
            <Select value={String(anno)} onValueChange={(v) => setAnno(parseInt(v))}>
              <SelectTrigger className="w-[100px]" data-testid="anno-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {anni.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button asChild variant="outline" size="icon" data-testid="scadenze-bell">
              <Link to="/pratiche" className="relative" title="Vai agli incarichi">
                <ClipboardText size={16} weight="regular" />
              </Link>
            </Button>
            <Button asChild data-testid="nuova-pratica-shortcut">
              <Link to="/pratiche"><Plus size={16} weight="bold" /> Nuova Pratica</Link>
            </Button>
          </div>
        }
      />

      {(professionista !== "all" || tipoPratica !== "all") && (
        <div className="mb-4 flex items-center gap-2 flex-wrap" data-testid="active-filters">
          <span className="text-xs font-mono uppercase tracking-widest text-slate-500">Filtri attivi:</span>
          {professionista !== "all" && (
            <button
              onClick={() => setProfessionista("all")}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-slate-900 text-white text-xs font-medium hover:bg-slate-700 transition-colors"
              data-testid="chip-professionista"
            >
              {members.find((m) => m.id === professionista)?.name || "Tecnico"}
              <span className="text-slate-400">×</span>
            </button>
          )}
          {tipoPratica !== "all" && (
            <button
              onClick={() => setTipoPratica("all")}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-slate-900 text-white text-xs font-medium hover:bg-slate-700 transition-colors capitalize"
              data-testid="chip-tipo"
            >
              {tipoPratica}
              <span className="text-slate-400">×</span>
            </button>
          )}
          <button
            onClick={() => { setProfessionista("all"); setTipoPratica("all"); }}
            className="text-xs text-slate-500 hover:text-slate-900 underline underline-offset-2"
            data-testid="clear-filters"
          >
            Rimuovi tutti
          </button>
        </div>
      )}

      {/* --- Widget Fatturato ultimo trimestre --- */}
      {(() => {
        const mesi = stats?.incassi_mensili_anno || [];
        // Prendi gli ultimi 3 mesi con dati (incassato o da_incassare > 0), o gli ultimi 3 assoluti
        const nonZero = mesi.filter((m) => (m.incassato || 0) + (m.da_incassare || 0) > 0);
        const ymNow = new Date().toISOString().slice(0, 7);
        const finoAdOggi = mesi.filter((m) => !m.chiave || m.chiave <= ymNow);
        const last3 = (nonZero.length >= 3 ? nonZero : finoAdOggi.length ? finoAdOggi : mesi).slice(-3);
        const totIncassato = last3.reduce((s, m) => s + (m.incassato || 0), 0);
        const totDaIncassare = last3.reduce((s, m) => s + (m.da_incassare || 0), 0);
        const totFatturato = totIncassato + totDaIncassare;
        const maxVal = Math.max(1, ...last3.map((m) => (m.incassato || 0) + (m.da_incassare || 0)));
        if (last3.length === 0) return null;
        return (
          <Card className="p-5 border-slate-200 shadow-none mb-4 overflow-hidden" data-testid="widget-trimestre">
            <div className="flex flex-col sm:flex-row items-stretch sm:items-start justify-between gap-4 sm:gap-6">
              <div className="sm:flex-shrink-0 min-w-0">
                <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500 mb-1">Fatturato ultimo trimestre</div>
                <div className="font-heading text-3xl font-bold tabular-nums text-slate-900 leading-none">{fmtEUR(totFatturato)}</div>
                <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs">
                  <span className="inline-flex items-center gap-1.5 text-emerald-700"><span className="h-2 w-2 rounded-sm bg-emerald-600" />Incassato <b className="tabular-nums">{fmtEUR(totIncassato)}</b></span>
                  <span className="inline-flex items-center gap-1.5 text-amber-700"><span className="h-2 w-2 rounded-sm bg-amber-500" />Da incassare <b className="tabular-nums">{fmtEUR(totDaIncassare)}</b></span>
                </div>
              </div>
              {/* Mini bar chart */}
              <div className="flex-1 flex items-end justify-between sm:justify-end gap-2 sm:gap-3 h-24 w-full sm:max-w-md">
                {last3.map((m) => {
                  const incassato = m.incassato || 0;
                  const daIncassare = m.da_incassare || 0;
                  const tot = incassato + daIncassare;
                  const pctInc = tot > 0 ? (incassato / maxVal) * 100 : 0;
                  const pctDaInc = tot > 0 ? (daIncassare / maxVal) * 100 : 0;
                  return (
                    <button
                      key={m.chiave || m.mese}
                      onClick={() => openMonth(m)}
                      className="flex flex-col items-center gap-1 group flex-1 max-w-[80px]"
                      data-testid={`widget-tri-bar-${m.chiave || m.mese}`}
                      title={`${m.mese}: ${fmtEUR2(tot)}`}
                    >
                      <div className="text-[10px] font-mono tabular-nums text-slate-500 group-hover:text-slate-900">{fmtEUR(tot)}</div>
                      <div className="w-full flex flex-col justify-end h-16 bg-slate-50 rounded-t-md overflow-hidden">
                        <div className="w-full bg-amber-500 transition-all" style={{ height: `${pctDaInc}%` }} />
                        <div className="w-full bg-emerald-600 transition-all" style={{ height: `${pctInc}%` }} />
                      </div>
                      <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500 group-hover:text-slate-900">{m.mese}</div>
                    </button>
                  );
                })}
              </div>
            </div>
          </Card>
        );
      })()}

      {/* Hero KPI - 3 cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <HeroKPI
          label="Prodotto Totale"
          value={fmtEUR(stats?.prodotto_totale)}
          sub={`${stats?.pratiche_totali_anno || 0} pratiche · ${anno}`}
          icon={TrendUp}
          tone="dark"
          testid="hero-prodotto"
        />
        <HeroKPI
          label="Incassato"
          value={fmtEUR(stats?.incassato_totale)}
          sub={`${stats?.incassi_count || 0} incassi registrati`}
          icon={CheckCircle}
          tone="green"
          testid="hero-incassato"
        />
        <HeroKPI
          label="Da Incassare"
          value={fmtEUR(stats?.da_incassare_totale)}
          sub={`${stats?.da_incassare_count || 0} fatture aperte`}
          icon={Hourglass}
          tone="amber"
          testid="hero-da-incassare"
        />
      </div>

      {/* Cruscotto scadenze: incarichi in scadenza · preventivi in attesa · fatture da incassare */}
      <AvvisoBackup />
      <CruscottoScadenze />

      {/* Avviso: sopralluoghi obbligatori DL/CSE non ancora registrati */}
      <AdempimentiImminenti />

      <AvvisoSopralluoghi />

      {/* --- Sezione operativa: Pratiche da fare / Cantieri in corso / Prossimi appuntamenti --- */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        {/* Pratiche da fare */}
        <Card className="p-0 border-slate-200 shadow-none overflow-hidden" data-testid="dash-pratiche-da-fare">
          <div className="p-4 border-b border-slate-200 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="h-8 w-8 rounded-md bg-red-50 text-red-700 flex items-center justify-center"><ClipboardText size={16} /></div>
              <div>
                <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500">Priorità</div>
                <h3 className="font-heading font-semibold text-sm">Pratiche da fare</h3>
              </div>
            </div>
            <Link to="/pratiche" className="text-[10px] font-mono uppercase tracking-widest text-slate-500 hover:text-slate-900" data-testid="dash-view-all-pratiche">
              {pratichePendenti.filter((p) => p.stato === "da_fare").length} tot
            </Link>
          </div>
          <div className="divide-y divide-slate-100 max-h-72 overflow-y-auto">
            {pratcheDaFare.length === 0 ? (
              <div className="p-6 text-center text-xs text-slate-500 italic">Nessuna pratica da fare ✓</div>
            ) : pratcheDaFare.map((p) => (
              <Link key={p.id} to={`/pratiche/${p.id}`} className="flex items-center gap-3 p-3 hover:bg-slate-50 transition-colors" data-testid={`dash-pratica-${p.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="text-sm font-semibold text-slate-900 truncate uppercase">{clientiMap[p.cliente_id] || "—"}</div>
                    {p.priorita === "alta" && <span className="text-[9px] font-mono uppercase bg-red-100 text-red-800 rounded px-1 py-0.5">Alta</span>}
                  </div>
                  <div className="text-[11px] text-slate-500 truncate mt-0.5">{p.titolo}</div>
                  <div className="text-[10px] font-mono text-slate-500 mt-0.5 truncate">
                    {p.tipo_label || p.tipo} {p.data_incarico && `· incarico ${fmtDayShort(p.data_incarico)}`}
                  </div>
                </div>
                <CaretRight size={12} className="text-slate-300 flex-shrink-0" />
              </Link>
            ))}
          </div>
        </Card>

        {/* Cantieri in corso */}
        <Card className="p-0 border-slate-200 shadow-none overflow-hidden" data-testid="dash-cantieri">
          <div className="p-4 border-b border-slate-200 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="h-8 w-8 rounded-md bg-amber-50 text-amber-700 flex items-center justify-center"><HardHat size={16} /></div>
              <div>
                <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500">Sul campo</div>
                <h3 className="font-heading font-semibold text-sm">Cantieri in corso</h3>
              </div>
            </div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">
              {pratichePendenti.filter((p) => isCantierePratica(p) && p.stato === "in_corso").length} tot
            </div>
          </div>
          <div className="divide-y divide-slate-100 max-h-72 overflow-y-auto">
            {cantieriAttivi.length === 0 ? (
              <div className="p-6 text-center text-xs text-slate-500 italic">Nessun cantiere in corso</div>
            ) : cantieriAttivi.map((p) => {
              const fine = p.data_fine_lavori_presunta;
              const gg = fine ? Math.round((new Date(fine) - now) / 86400000) : null;
              return (
                <Link key={p.id} to={`/pratiche/${p.id}`} className="flex items-center gap-3 p-3 hover:bg-slate-50 transition-colors" data-testid={`dash-cantiere-${p.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-slate-900 truncate uppercase">{clientiMap[p.cliente_id] || "—"}</div>
                    <div className="text-[11px] text-slate-500 truncate mt-0.5">{p.titolo}</div>
                    <div className="text-[10px] font-mono text-slate-500 mt-0.5 truncate">
                      {p.data_inizio_lavori && `dal ${fmtDayShort(p.data_inizio_lavori)}`}
                      {fine && ` → ${fmtDayShort(fine)}`}
                      {gg !== null && gg < 0 && <span className="text-red-600 ml-1">· ritardo {Math.abs(gg)}gg</span>}
                      {gg !== null && gg >= 0 && gg <= 7 && <span className="text-amber-700 ml-1">· fine tra {gg}gg</span>}
                    </div>
                  </div>
                  <CaretRight size={12} className="text-slate-300 flex-shrink-0" />
                </Link>
              );
            })}
          </div>
        </Card>

        {/* Prossimi appuntamenti */}
        <Card className="p-0 border-slate-200 shadow-none overflow-hidden" data-testid="dash-appuntamenti">
          <div className="p-4 border-b border-slate-200 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="h-8 w-8 rounded-md bg-blue-50 text-blue-700 flex items-center justify-center"><CalendarBlank size={16} /></div>
              <div>
                <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500">Prossimi 7 giorni</div>
                <h3 className="font-heading font-semibold text-sm">Appuntamenti</h3>
              </div>
            </div>
            <Link to="/calendario" className="text-[10px] font-mono uppercase tracking-widest text-slate-500 hover:text-slate-900" data-testid="dash-view-calendar">Calendario →</Link>
          </div>
          <div className="divide-y divide-slate-100 max-h-72 overflow-y-auto">
            {prossimiAppuntamenti.length === 0 ? (
              <div className="p-6 text-center text-xs text-slate-500 italic">Nessun appuntamento nei prossimi 7 giorni</div>
            ) : prossimiAppuntamenti.map((a) => (
              <Link key={a.id} to="/calendario" className="flex items-center gap-3 p-3 hover:bg-slate-50 transition-colors" data-testid={`dash-app-${a.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-900 truncate">{a.titolo}</div>
                  <div className="text-[10px] font-mono text-slate-500 mt-0.5 truncate">{fmtDayTime(a.data_ora)} {a.luogo && `· ${a.luogo}`}</div>
                </div>
                <CaretRight size={12} className="text-slate-300 flex-shrink-0" />
              </Link>
            ))}
          </div>
        </Card>
      </div>

      {/* --- Riepilogo stati pratiche (mini KPIs) --- */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {[
          { k: "da_fare", label: "Da fare", tone: "red" },
          { k: "in_attesa", label: "In attesa", tone: "blue" },
          { k: "in_corso", label: "In corso", tone: "amber" },
          { k: "completata", label: "Completate", tone: "green" },
        ].map((s) => {
          const count = pratiche.filter((p) => p.stato === s.k).length;
          const toneMap = {
            red: "bg-red-50 border-red-200 text-red-700",
            blue: "bg-blue-50 border-blue-200 text-blue-700",
            amber: "bg-amber-50 border-amber-200 text-amber-700",
            green: "bg-emerald-50 border-emerald-200 text-emerald-700",
          };
          return (
            <Card key={s.k} className={`p-4 border shadow-none ${toneMap[s.tone]}`} data-testid={`dash-stato-${s.k}`}>
              <div className="text-[10px] font-mono uppercase tracking-[0.2em] mb-1">{s.label}</div>
              <div className="font-heading text-2xl font-bold tabular-nums">{count}</div>
              <div className="text-[10px] font-mono opacity-80 mt-1">{s.k === "completata" ? "questo anno" : "aperte"}</div>
            </Card>
          );
        })}
      </div>

      {/* Cosa devo fare oggi */}
      {(() => {
        const today = new Date().toISOString().slice(0, 10);
        const in7 = new Date(Date.now() + 7*86400000).toISOString().slice(0, 10);
        const appuntamentiOggi = (appuntamenti || []).filter(a => (a.data || "").startsWith(today));
        const fasiScad = [];
        (pratiche || []).forEach(p => {
          if (!["in_corso","in_attesa"].includes(p.stato)) return;
          (p.fasi || []).forEach(f => {
            if (f.completata || !f.data_scadenza) return;
            if (f.data_scadenza <= in7) fasiScad.push({ ...f, praticaTitolo: p.titolo, pid: p.id });
          });
        });
        const totaleOggi = appuntamentiOggi.length + fasiScad.length;
        if (totaleOggi === 0) return null;
        return (
          <Card className="border-slate-200 shadow-none mb-6 overflow-hidden border-l-4 border-l-blue-500" data-testid="oggi-widget">
            <div className="p-4 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-8 w-8 rounded-md bg-blue-50 text-blue-700 flex items-center justify-center"><CheckCircle size={16} weight="regular" /></div>
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500 mb-0.5">Oggi</div>
                  <div className="text-sm font-semibold text-slate-900">Cosa devo fare — {totaleOggi} attività</div>
                </div>
              </div>
            </div>
            <div className="divide-y divide-slate-100">
              {appuntamentiOggi.slice(0, 3).map((a, i) => (
                <div key={`app-${i}`} className="flex items-center gap-3 p-3">
                  <span className="h-2 w-2 rounded-full bg-blue-500 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-slate-900 truncate">📅 {a.titolo || a.oggetto || "Appuntamento"}</div>
                    <div className="text-[11px] font-mono text-slate-500">{new Date(a.data).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}</div>
                  </div>
                </div>
              ))}
              {fasiScad.slice(0, 6).map((f, i) => (
                <Link key={`fase-${i}`} to={`/pratiche/${f.pid}`} className="flex items-center gap-3 p-3 hover:bg-slate-50" data-testid={`oggi-fase-${i}`}>
                  <span className="h-2 w-2 rounded-full bg-amber-500 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-slate-900 truncate">✅ {f.nome} <span className="text-slate-400">·</span> <span className="text-xs">{f.praticaTitolo}</span></div>
                    <div className="text-[11px] font-mono text-slate-500">Scade {new Date(f.data_scadenza).toLocaleDateString("it-IT")}</div>
                  </div>
                </Link>
              ))}
            </div>
          </Card>
        );
      })()}

      {/* Radar Pratiche - proactive alerts */}
      <RadarPratiche />

      {/* Watchdog - alert critici aggregati */}
      {watchdogAlerts.length > 0 && (
        <Card className="border-slate-200 shadow-none mb-6 overflow-hidden border-l-4 border-l-red-600" data-testid="watchdog-alerts-card">
          <div className="p-4 border-b border-slate-100 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-8 w-8 rounded-md bg-red-50 text-red-700 flex items-center justify-center"><Warning size={16} weight="regular" /></div>
              <div>
                <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500 mb-0.5">Watchdog</div>
                <div className="text-sm font-semibold text-slate-900">Cose che richiedono la tua attenzione</div>
              </div>
            </div>
            <div className="text-xs font-mono text-slate-500">{watchdogAlerts.length} avvisi</div>
          </div>
          <div className="divide-y divide-slate-100">
            {watchdogAlerts.slice(0, 8).map((a, i) => {
              const iconCls = a.tone === "red" ? "text-red-700 bg-red-50 border-red-200"
                : "text-amber-700 bg-amber-50 border-amber-200";
              const dotCls = a.tone === "red" ? "bg-red-500" : "bg-amber-500";
              const tipoLabel = a.tipo === "scadenza_legale" ? "Scadenza legale"
                : a.tipo === "pratica_ferma" ? "Pratica ferma"
                : a.tipo === "fattura_insoluta" ? "Fattura insoluta"
                : a.tipo === "protocollo_scadenza" ? "Protocollo"
                : "Avviso";
              const link = a.fattura_id ? "/fatturazione" : (a.pratica_id ? `/pratiche/${a.pratica_id}` : "#");
              return (
                <Link key={i} to={link} className="flex items-center justify-between p-3 hover:bg-slate-50" data-testid={`watchdog-alert-${i}`}>
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <span className={`h-2 w-2 rounded-full flex-shrink-0 ${dotCls}`}></span>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-slate-900 truncate">{a.titolo}</div>
                      <div className="text-[11px] font-mono text-slate-500 truncate">{a.sottotitolo}</div>
                    </div>
                  </div>
                  <span className={`text-[10px] px-2 py-1 rounded-full border font-medium ${iconCls} flex-shrink-0 ml-2`}>{tipoLabel}</span>
                </Link>
              );
            })}
          </div>
        </Card>
      )}

      {/* Le mie scadenze professionali */}
      {scadenzeProf.length > 0 && (
        <Card className="border-slate-200 shadow-none mb-6 overflow-hidden border-l-4 border-l-amber-500" data-testid="scadenze-prof-card">
          <div className="p-4 border-b border-slate-100 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className="h-8 w-8 rounded-md bg-amber-50 text-amber-700 flex items-center justify-center flex-shrink-0"><Warning size={16} weight="regular" /></div>
              <div className="min-w-0">
                <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500 mb-0.5">Adempimenti del tecnico</div>
                <div className="text-sm font-semibold text-slate-900 truncate">Le mie scadenze da sistemare</div>
              </div>
            </div>
            <div className="text-xs font-mono text-slate-500 flex-shrink-0">{scadenzeProf.length}</div>
          </div>
          <div className="divide-y divide-slate-100">
            {scadenzeProf.slice(0, 5).map((s) => {
              const cls = s.stato === "in_scadenza" ? "text-amber-700 bg-amber-50 border-amber-200" : "text-red-700 bg-red-50 border-red-200";
              const label = s.giorni_rimanenti < 0 ? `Scaduta da ${Math.abs(s.giorni_rimanenti)}gg`
                : s.giorni_rimanenti === 0 ? "Scade oggi" : `Tra ${s.giorni_rimanenti}gg`;
              const cfp = s.tipo === "crediti_formativi" && s.crediti_richiesti
                ? ` · CFP ${s.crediti_acquisiti || 0}/${s.crediti_richiesti}` : "";
              return (
                <Link key={s.id} to="/scadenze-professionali" className="flex items-center justify-between p-3 hover:bg-slate-50" data-testid={`scadenza-prof-alert-${s.id}`}>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-900 truncate">{s.descrizione || s.tipo_label}</div>
                    <div className="text-[11px] font-mono text-slate-500">
                      {s.tipo_label} · {new Date(`${String(s.scadenza).slice(0, 10)}T00:00:00`).toLocaleDateString("it-IT")}{cfp}
                    </div>
                  </div>
                  <span className={`text-[10px] px-2 py-1 rounded-full border font-medium flex-shrink-0 ml-2 ${cls}`}>{label}</span>
                </Link>
              );
            })}
          </div>
        </Card>
      )}

      {/* DURC alerts */}
      {durcAlerts.length > 0 && (
        <Card className="border-slate-200 shadow-none mb-6 overflow-hidden border-l-4 border-l-red-500" data-testid="durc-alerts-card">
          <div className="p-4 border-b border-slate-100 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-8 w-8 rounded-md bg-red-50 text-red-700 flex items-center justify-center"><Warning size={16} weight="regular" /></div>
              <div>
                <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500 mb-0.5">Alert imprese</div>
                <div className="text-sm font-semibold text-slate-900">Documenti imprese scaduti o in scadenza (30 giorni)</div>
              </div>
            </div>
            <div className="text-xs font-mono text-slate-500">{durcAlerts.length} documenti</div>
          </div>
          <div className="divide-y divide-slate-100">
            {durcAlerts.slice(0, 5).map((a) => {
              const cls = a.stato === "scaduto" ? "text-red-700 bg-red-50 border-red-200"
                : a.stato === "critico" ? "text-red-700 bg-red-50 border-red-200"
                : "text-amber-700 bg-amber-50 border-amber-200";
              const label = a.stato === "scaduto" ? `Scaduto da ${Math.abs(a.giorni_rimanenti)}gg`
                : a.stato === "critico" ? `In scadenza tra ${a.giorni_rimanenti}gg`
                : `In scadenza tra ${a.giorni_rimanenti}gg`;
              return (
                <Link key={`${a.id}-${a.documento || "durc"}-${a.scadenza || a.durc_scadenza}`} to="/imprese" className="flex items-center justify-between p-3 hover:bg-slate-50" data-testid={`durc-alert-${a.id}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <Buildings size={14} className="text-slate-500 flex-shrink-0" />
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-slate-900 truncate">
                        {a.ragione_sociale}
                        <span className="text-slate-500 font-normal"> · {a.documento || "DURC"}</span>
                      </div>
                      <div className="text-[11px] font-mono text-slate-500">Scadenza: {new Date(a.scadenza || a.durc_scadenza).toLocaleDateString("it-IT")}</div>
                    </div>
                  </div>
                  <span className={`text-[10px] px-2 py-1 rounded-full border font-medium ${cls}`}>{label}</span>
                </Link>
              );
            })}
          </div>
        </Card>
      )}


      {/* Status stripe: pratiche senza scadenza */}
      <Card className={`p-4 border-slate-200 shadow-none mb-6 ${(stats?.pratiche_senza_scadenza || 0) === 0 ? "" : "border-l-4 border-l-amber-500"}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {(stats?.pratiche_senza_scadenza || 0) === 0 ? (
              <>
                <div className="h-8 w-8 rounded-md bg-emerald-50 text-emerald-700 flex items-center justify-center"><CheckCircle size={16} weight="fill" /></div>
                <div>
                  <div className="text-sm font-medium text-slate-900">Tutte le pratiche aperte hanno una scadenza</div>
                  <div className="text-xs text-slate-500">Sei perfettamente organizzato.</div>
                </div>
              </>
            ) : (
              <>
                <div className="h-8 w-8 rounded-md bg-amber-50 text-amber-700 flex items-center justify-center"><Warning size={16} weight="regular" /></div>
                <div>
                  <div className="text-sm font-medium text-slate-900">
                    <span className="font-mono">{stats?.pratiche_senza_scadenza}</span> pratich{stats?.pratiche_senza_scadenza === 1 ? "a" : "e"} senza data di scadenza
                  </div>
                  <div className="text-xs text-slate-500">Definisci le scadenze per non perdere il controllo.</div>
                </div>
              </>
            )}
          </div>
          <Button asChild variant="ghost" size="sm">
            <Link to="/pratiche" className="text-xs">Gestisci</Link>
          </Button>
        </div>
      </Card>

      {/* Two charts side-by-side */}
      {stats && <Suspense fallback={<div className="h-64 mb-6" />}><DashboardCharts stats={stats} anno={anno} meseTop={meseTop} openMonth={openMonth} /></Suspense>}

      {/* Incarichi in scadenza + AI Advisor */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <Card className="lg:col-span-2 border-slate-200 shadow-none overflow-hidden" data-testid="scadenze-panel">
          <div className="p-5 border-b border-slate-200 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="h-8 w-8 rounded-md bg-amber-50 text-amber-700 flex items-center justify-center"><Warning size={16} weight="regular" /></div>
              <div>
                <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500 mb-0.5">Incarichi</div>
                <h3 className="font-heading text-lg font-semibold">In Scadenza</h3>
              </div>
            </div>
            <div className="text-xs font-mono text-slate-500">{scadenze.length} attivi</div>
          </div>
          {scadenze.length === 0 ? (
            <div className="p-10 text-center text-sm text-slate-500">Nessuna scadenza imminente. Ottimo lavoro.</div>
          ) : (
            <div className="divide-y divide-slate-100">
              {scadenze.map((p) => {
                const r = relativeDay(p.giorni);
                const dateLabel = p.scadenza ? new Date(p.scadenza).toLocaleDateString("it-IT", { day: "numeric", month: "short" }) : "—";
                return (
                  <Link key={p.id} to={`/pratiche/${p.id}`} className="flex items-center justify-between p-4 hover:bg-slate-50 transition-colors" data-testid={`scadenza-${p.id}`}>
                    <div className="min-w-0 flex-1 pr-4">
                      <div className="font-medium text-sm text-slate-900 truncate">{p.titolo}</div>
                      <div className="text-xs text-slate-500 mt-0.5 font-mono uppercase tracking-wider">{p.tipo || "generica"}</div>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <PriorityBadge priority={p.priorita} />
                      <div className="text-right">
                        <div className="text-xs font-mono text-slate-500">{dateLabel}</div>
                        <div className={`text-[11px] font-semibold ${
                          r.tone === "red" ? "text-red-700" : r.tone === "amber" ? "text-amber-700" : "text-slate-600"
                        }`}>{r.label}</div>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </Card>

        {/* AI Advisor */}
        <Card className="border-slate-900 bg-slate-900 text-white shadow-none overflow-hidden" data-testid="ai-advisor-card">
          <div className="p-5 border-b border-slate-800">
            <div className="flex items-center gap-2.5 mb-2">
              <div className="h-8 w-8 rounded-md bg-white/10 flex items-center justify-center"><Sparkle size={16} weight="regular" /></div>
              <div>
                <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-400 mb-0.5">Assistente</div>
                <h3 className="font-heading text-base font-semibold">AI Advisor Studio</h3>
              </div>
            </div>
            <p className="text-xs text-slate-400 leading-relaxed">Genera un report economico dettagliato dell&apos;anno con suggerimenti pratici.</p>
          </div>
          <div className="p-5 space-y-3">
            {!report && (
              <>
                <Select value={String(reportYear)} onValueChange={(v) => setReportYear(parseInt(v))}>
                  <SelectTrigger data-testid="report-year-select" className="bg-white/5 border-white/10 text-white"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {anni.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button onClick={generateReport} disabled={reportLoading} className="w-full bg-white text-slate-900 hover:bg-slate-100" data-testid="generate-report-button">
                  {reportLoading ? "Analisi in corso..." : (<><Sparkle size={14} weight="bold" /> Genera Report AI</>)}
                </Button>
                <p className="text-[11px] text-slate-500 leading-relaxed">L&apos;AI analizza fatturato, incassi, insoluti e clienti dello studio per suggerirti le prossime azioni.</p>
              </>
            )}
            {report && (
              <>
                <div className="max-h-[300px] overflow-y-auto text-xs leading-relaxed whitespace-pre-wrap font-mono text-slate-100 scrollbar-thin" data-testid="ai-report-output">
                  {report}
                </div>
                <Button onClick={() => setReport("")} variant="outline" className="w-full bg-transparent text-white border-white/20 hover:bg-white/10 hover:text-white">
                  Nuova analisi
                </Button>
              </>
            )}
          </div>
        </Card>
      </div>

      {/* Status pills */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <StatoPill label="In Corso" count={stats?.stato_counts?.in_corso || 0} icon={Clock} tone="blue" testid="stato-in-corso" />
        <StatoPill label="In Attesa" count={stats?.stato_counts?.in_attesa || 0} icon={Hourglass} tone="amber" testid="stato-in-attesa" />
        <StatoPill label="Completate" count={stats?.stato_counts?.completata || 0} icon={CheckCircle} tone="green" testid="stato-completate" />
        <StatoPill label="Sospese" count={stats?.stato_counts?.sospesa || 0} icon={PauseCircle} tone="red" testid="stato-sospese" />
      </div>

      {/* Bottom stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <Card className="p-5 border-slate-200 shadow-none flex items-center gap-4">
          <div className="h-10 w-10 rounded-md bg-slate-100 text-slate-700 flex items-center justify-center"><UsersThree size={18} weight="regular" /></div>
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500">Clienti</div>
            <div className="font-heading text-2xl font-bold tabular-nums" data-testid="stat-clienti">{stats?.clienti_totali ?? "—"}</div>
          </div>
        </Card>
        <Card className="p-5 border-slate-200 shadow-none flex items-center gap-4">
          <div className="h-10 w-10 rounded-md bg-slate-100 text-slate-700 flex items-center justify-center"><ClipboardText size={18} weight="regular" /></div>
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500">Pratiche totali</div>
            <div className="font-heading text-2xl font-bold tabular-nums" data-testid="stat-pratiche-totali">{stats?.pratiche_totali ?? "—"}</div>
          </div>
        </Card>
        <Card className="p-5 border-slate-200 shadow-none flex items-center gap-4">
          <div className="h-10 w-10 rounded-md bg-slate-100 text-slate-700 flex items-center justify-center"><CurrencyEur size={18} weight="regular" /></div>
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500">Incassi mese</div>
            <div className="font-heading text-2xl font-bold tabular-nums" data-testid="incassi-mese">{fmtEUR2(stats?.incassi_mese)}</div>
          </div>
        </Card>
      </div>

      <Dialog open={!!monthDialog} onOpenChange={(o) => { if (!o) { setMonthDialog(null); setMonthFatture([]); } }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              Fatture di {monthDialog?.mese} {monthDialog?.anno}
              {monthFatture.length > 0 && (
                <span className="ml-2 text-xs font-mono uppercase tracking-widest text-slate-500">
                  {monthFatture.length} fatture · {fmtEUR2(monthFatture.reduce((s, f) => s + (f.totale || 0), 0))}
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          {monthLoading ? (
            <div className="p-8 text-center text-sm text-slate-500">Caricamento...</div>
          ) : monthFatture.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-500">Nessuna fattura in questo mese.</div>
          ) : (
            <div className="max-h-[400px] overflow-y-auto scrollbar-thin -mx-6">
              <table className="w-full text-sm" data-testid="month-fatture-table">
                <thead className="bg-slate-50 border-y border-slate-200 sticky top-0">
                  <tr className="text-left">
                    <th className="py-2 px-6 font-mono text-[10px] uppercase tracking-widest text-slate-500">Numero</th>
                    <th className="py-2 px-2 font-mono text-[10px] uppercase tracking-widest text-slate-500">Data</th>
                    <th className="py-2 px-2 font-mono text-[10px] uppercase tracking-widest text-slate-500">Stato</th>
                    <th className="py-2 px-6 font-mono text-[10px] uppercase tracking-widest text-slate-500 text-right">Totale</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {monthFatture.map((f) => (
                    <tr key={f.id} className="hover:bg-slate-50" data-testid={`month-fattura-${f.id}`}>
                      <td className="py-2 px-6 font-mono font-medium text-slate-900">{f.numero}</td>
                      <td className="py-2 px-2 font-mono text-xs text-slate-600">{new Date(f.data).toLocaleDateString("it-IT")}</td>
                      <td className="py-2 px-2">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-medium ${
                          f.stato === "pagata" ? "bg-emerald-50 text-emerald-700" :
                          f.stato === "sollecitata" ? "bg-red-50 text-red-700" :
                          f.stato === "annullata" ? "bg-slate-100 text-slate-600" :
                          "bg-amber-50 text-amber-700"
                        }`}>{f.stato}</span>
                      </td>
                      <td className="py-2 px-6 text-right font-mono font-semibold tabular-nums">{fmtEUR2(f.totale)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="px-6 py-3 border-t border-slate-200 flex justify-end">
                <Button asChild variant="outline" size="sm">
                  <Link to="/fatturazione">Apri Fatturazione</Link>
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

    </div>
  );
}
